import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import { AlertTriangle, Download, Loader2, Lock, Search, SlidersHorizontal } from "lucide-react";

const SORT_OPTIONS = [
  { value: "", label: "Mais recentes" },
  { value: "downloads", label: "Mais baixadas" },
  { value: "title", label: "Titulo A-Z" },
];

const MOOD_OPTIONS = [
  { value: "", label: "Todos os moods" },
  { value: "energetic", label: "Energetico" },
  { value: "emotional", label: "Emocional" },
  { value: "cinematic", label: "Cinematico" },
  { value: "dark", label: "Dark" },
  { value: "uplifting", label: "Inspirador" },
];

const GENRE_OPTIONS = [
  { value: "", label: "Todos os generos" },
  { value: "phonk", label: "Phonk" },
  { value: "trap", label: "Trap" },
  { value: "cinematic", label: "Cinematico" },
  { value: "funk", label: "Funk" },
  { value: "pop", label: "Pop" },
];

const USAGE_OPTIONS = [
  { value: "", label: "Todos os usos" },
  { value: "viral", label: "Viral" },
  { value: "storytelling", label: "Storytelling" },
  { value: "transition", label: "Transicao" },
  { value: "motivation", label: "Motivacional" },
  { value: "general", label: "Geral" },
];

const ENERGY_OPTIONS = [
  { value: "", label: "Todas as energias" },
  { value: "high", label: "Alta" },
  { value: "medium", label: "Media" },
  { value: "low", label: "Baixa" },
];

const SoundsView = () => {
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    mood: "",
    genre: "",
    usage_type: "",
    energy_level: "",
    sort: "",
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadSounds();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const loadSounds = async () => {
    try {
      setLoading(true);
      setError("");
      setLocked(false);

      const query = {};
      if (filters.search.trim()) query.search = filters.search.trim();
      if (filters.mood) query.mood = filters.mood;
      if (filters.genre) query.genre = filters.genre;
      if (filters.usage_type) query.usage_type = filters.usage_type;
      if (filters.energy_level) query.energy_level = filters.energy_level;
      if (filters.sort) query.sort = filters.sort;

      const data = await api.getSounds(query);
      setSounds(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.message || "Erro ao carregar musicas.";
      if (msg.includes("403") || msg.toLowerCase().includes("acesso liberado")) {
        setLocked(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      mood: "",
      genre: "",
      usage_type: "",
      energy_level: "",
      sort: "",
    });
  };

  const handleDownload = async (soundId) => {
    if (downloadingId) return;
    setDownloadError("");
    setDownloadingId(soundId);
    try {
      await api.downloadSound(soundId);
    } catch (e) {
      const msg = e?.message || "";
      if (msg === "session_expired") {
        setDownloadError("Sessao expirada. Recarregue a pagina.");
      } else {
        setDownloadError("Erro ao baixar. Tente novamente.");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0056D2]" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center border border-white/5 bg-white/[0.01] rounded-[40px] p-10 text-center relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#0056D2]/10 blur-[100px] rounded-full" />
        <div className="relative z-10">
          <div className="w-24 h-24 bg-gradient-to-b from-white/10 to-transparent rounded-3xl flex items-center justify-center mb-8 border border-white/10 mx-auto shadow-2xl">
            <Lock className="w-10 h-10 text-[#0056D2] animate-pulse" />
          </div>
          <h2 className="text-4xl font-bold mb-4 italic uppercase tracking-tighter">
            Acesso Bloqueado
          </h2>
          <p className="text-slate-400 max-w-md mb-2 mx-auto font-bold">
            As musicas virais sao liberadas apos 7 dias ou liberacao manual.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 backdrop-blur-xl bg-red-500/10 rounded-2xl border border-red-500/20">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <span className="text-red-300 text-sm">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <SlidersHorizontal className="w-4 h-4 text-[#0056D2]" />
          <p className="text-white text-sm font-bold uppercase tracking-[0.2em]">Filtros</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
          <label className="xl:col-span-2">
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Busca</span>
            <div className="flex items-center gap-2 rounded-2xl border border-white/8 bg-black/20 px-3">
              <Search className="w-4 h-4 text-white/30" />
              <input
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Titulo, mood, genero..."
                className="w-full bg-transparent py-3 text-white text-sm outline-none placeholder:text-white/20"
              />
            </div>
          </label>

          <label>
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Mood</span>
            <select
              value={filters.mood}
              onChange={(e) => handleFilterChange("mood", e.target.value)}
              className="w-full rounded-2xl border border-white/8 bg-black/20 px-3 py-3 text-white text-sm outline-none"
            >
              {MOOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A0A12]">
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Genero</span>
            <select
              value={filters.genre}
              onChange={(e) => handleFilterChange("genre", e.target.value)}
              className="w-full rounded-2xl border border-white/8 bg-black/20 px-3 py-3 text-white text-sm outline-none"
            >
              {GENRE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A0A12]">
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Ordenar</span>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange("sort", e.target.value)}
              className="w-full rounded-2xl border border-white/8 bg-black/20 px-3 py-3 text-white text-sm outline-none"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A0A12]">
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Uso</span>
            <select
              value={filters.usage_type}
              onChange={(e) => handleFilterChange("usage_type", e.target.value)}
              className="w-full rounded-2xl border border-white/8 bg-black/20 px-3 py-3 text-white text-sm outline-none"
            >
              {USAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A0A12]">
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-white/40 text-[11px] uppercase tracking-[0.18em] block mb-2">Energia</span>
            <select
              value={filters.energy_level}
              onChange={(e) => handleFilterChange("energy_level", e.target.value)}
              className="w-full rounded-2xl border border-white/8 bg-black/20 px-3 py-3 text-white text-sm outline-none"
            >
              {ENERGY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A0A12]">
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-4 py-3 text-white/70 text-sm font-medium transition-all"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </div>

      {downloadError && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-xs">{downloadError}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-white/50 text-xs uppercase tracking-[0.18em]">
          {sounds.length} musica{sounds.length === 1 ? "" : "s"} encontrada{sounds.length === 1 ? "" : "s"}
        </p>
      </div>

      {sounds.length === 0 ? (
        <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-8 text-center">
          <p className="text-white/40">Nenhuma musica encontrada com os filtros atuais.</p>
        </div>
      ) : (
        sounds.map((s) => (
          <div
            key={s._id || s.id}
            className="p-4 rounded-3xl border border-white/5 bg-white/[0.02]"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-bold truncate">{s.title || "Sem titulo"}</p>
                <p className="text-white/40 text-xs mt-1">
                  {s.artist || "Artista desconhecido"}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {s.genre && (
                  <span className="px-2 py-1 rounded-full bg-white/[0.04] border border-white/8 text-white/60 text-[11px] uppercase">
                    {s.genre}
                  </span>
                )}
                {s.mood && (
                  <span className="px-2 py-1 rounded-full bg-[#0056D2]/10 border border-[#0056D2]/20 text-[#7FB0FF] text-[11px] uppercase">
                    {s.mood}
                  </span>
                )}
              </div>
            </div>

            <p className="text-white/40 text-xs mt-2">
              {s.format ? `Formato: ${s.format}` : ""}
              {s.duration ? ` | ${Math.round(s.duration)}s` : ""}
              {s.usage_type ? ` | Uso: ${s.usage_type}` : ""}
            </p>

            {s.audio_url && (
              <>
                <audio className="w-full mt-3" controls preload="none" src={s.stream_url || s.audio_url} />

                <button
                  onClick={() => handleDownload(s._id || s.id)}
                  disabled={!!downloadingId}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#0056D2] text-white font-bold hover:bg-[#0056D2]/90 transition-all disabled:opacity-60"
                >
                  {downloadingId === (s._id || s.id) ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  {downloadingId === (s._id || s.id) ? "Baixando..." : "Baixar musica"}
                </button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default SoundsView;
